<template>
  <div class="deal-tiles">
    <h2 class="mb-0">
      <icon-colored-exchange class="mr-2" />
      {{ capitalize($t('names.curatedDeal', { n: 2 })) }}
    </h2>
    <div class="row">
      <div class="col-12">
        <div class="pane-content table-container filters pt-3">
          <Table
            id="curated_packages_list_table_filters_for_tiles"
            ref="table"
            category="CuratedPackages"
            :items="curatedPackages"
            :columns="columns"
            :show-table="false"
            :is-loading="isLoading"
            :show-filters="true"
            :text-search-properties="['name', 'deal_id']"
            :total-count="totalCount"
            :sort-column="sortBy"
            :sort-ascending="isSortAscending"
            :is-main-table="false"
            :allow-selection="isSelectionEnabled"
            :reset-selection-on-update="false"
            :lock-selection="lockSelection"
            :can-select-item="isItemSelectable"
            pagination
            @filters-changed="(e) => tableFiltersChangedHandler(e)"
            @page-size-changed="(e) => emit('pageSizeChanged', e)"
            @selection-changed="(e) => emit('selectionChanged', e)">
            <template #extra-controls>
              <tiles-sort-control
                :sort-direction="sortDirection"
                :sort-by="sortBy"
                :sort-direction-options="sortDirectionOptions"
                :sort-by-options="sortByOptions"
                @sort-direction-changed="(e) => emit('sortDirectionChanged', e)"
                @sort-by-changed="(e) => emit('sortByChanged', e)" />
            </template>
          </Table>
        </div>
      </div>
    </div>

    <div
      v-if="isLoading"
      class="loader-container">
      <loader-small class="mr-3" />
      {{ capitalize($t('states.loading')) }}
    </div>

    <div v-else>
      <div class="curated-deal-tiles-grid">
        <curated-package-tile
          v-for="cp in curatedPackages"
          :key="cp.id"
          class="curated-package"
          :curated-deal="cp"
          :all-demographics="demographics"
          :all-media-owners="mediaOwners"
          :all-curators="curators"
          :buyer-organization-d-s-ps="buyerOrganizationDSPs"
          :can-select-item="!lockSelection && isItemSelectable(cp, selectedPackages)"
          :is-selected="isItemSelected(cp, selectedPackages)"
          @selection-changed="table.handleItemSelection(cp, !isItemSelected(cp, selectedPackages))" />
      </div>
      <table
        v-show="!isLoading && curatedPackages.length === 0"
        id="no-records-table"
        category="NoRecords"
        class="table">
        <tbody>
          <tr>
            <td>
              <no-records-table
                :icon="markRaw(IconComplexDeal2)"
                :header-text="$t('messages.tablePlaceholderNoCuratedDealsTitleDemandSize')"
                :details-text="$t('messages.tablePlaceholderNoCuratedDealsDescriptionDemandSize')" />
            </td>
          </tr>
        </tbody>
      </table>
      <table-footer
        v-if="totalCount > 10"
        class="mb-5"
        :total-count="totalCount"
        :page-index="pageIndex"
        :page-size="pageSize"
        @page-index-changed="(value) => (pageIndex = value)"
        @page-size-changed="(value) => emit('pageSizeChanged', value)" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, markRaw, ref, watch } from 'vue';

  import LoaderSmall from '@/common/components/controls/LoaderSmall.vue';
  import NoRecordsTable from '@/common/components/controls/table/NoRecordsTable.vue';
  import Table from '@/common/components/controls/table/Table.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import CuratedPackageTile from '@/common/components/deals/CuratedPackageTile.vue';
  import { isItemSelectable, isItemSelected } from '@/common/components/deals/DealsHelper';
  import { IconColoredExchange } from '@/common/icons/colored';
  import { IconComplexDeal2 } from '@/common/icons/complex';
  import { Demographic, OrganizationDSP, Package } from '@/common/models';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import CuratorOrganization from '@/common/models/misc-api-models/CuratorOrganization';
  import TableFooter from '@/org-app/components/controls/TableFooter.vue';
  import { ISortBy, ISortDirection } from '@/org-app/components/packages/DealSorting';
  import TilesSortControl from '@/org-app/components/packages/TilesSortControl.vue';

  // #region Props

  interface ICuratedDealTilesListProps {
    curators: CuratorOrganization[];
    curatedPackages: Package[];
    totalCount: number;
    sortBy?: ISortBy;
    isSortAscending?: boolean;
    isLoading: boolean;
    columns: Record<string, TableColumn>;
    pageSize: number;
    demographics: Demographic[];
    mediaOwners: MediaOwner[];
    buyerOrganizationDSPs: OrganizationDSP[];
    sortDirection?: ISortDirection;
    sortDirectionOptions?: ISortDirection[];
    sortByOptions?: ISortBy[];
    previousTableData?: typeof Table;
    isSelectionEnabled?: boolean;
    lockSelection?: boolean;
    selectedPackages?: Package[];
  }

  const props = withDefaults(defineProps<ICuratedDealTilesListProps>(), {
    curators: () => [],
    curatedPackages: () => [],
    totalCount: 0,
    sortBy: null,
    isSortAscending: true,
    isLoading: true,
    columns: null,
    pageSize: 10,
    demographics: () => [],
    mediaOwners: () => [],
    buyerOrganizationDSPs: () => [],
    sortDirection: null,
    sortDirectionOptions: null,
    sortByOptions: null,
    previousTableData: null,
    isSelectionEnabled: true,
    lockSelection: false,
    selectedPackages: () => [],
  });

  // #endregion

  // #region Emits

  const emit = defineEmits([
    'pageSizeChanged',
    'filtersChanged',
    'sortDirectionChanged',
    'sortByChanged',
    'loaded',
    'selectionChanged',
  ]);

  // #endregion

  //#region Data

  const table = ref(null);
  const restoredData = ref(props.previousTableData);
  const pageIndex = ref(props.previousTableData?.pageIndex ?? 1);
  const shouldUpdatePage = ref(true);

  //#endregion

  watch(
    () => pageIndex.value,
    () => {
      if (shouldUpdatePage.value) table.value?.setPageIndex(pageIndex.value);
    },
  );

  watch(
    () => props.pageSize,
    () => {
      table.value?.setPageSize(props.pageSize);
    },
  );

  watch(
    () => table.value,
    () => {
      if (table.value) emit('loaded', table.value);
    },
  );

  // #region Methods

  const tableFiltersChangedHandler = (x) => {
    if (restoredData.value) {
      restoredData.value = null;
      return;
    }
    if (table.value.pageIndex !== pageIndex.value) {
      shouldUpdatePage.value = false;
      pageIndex.value = table.value.pageIndex;
      shouldUpdatePage.value = true;
    }
    emit('filtersChanged', x);
  };

  // #endregion
</script>

<style lang="scss" scoped>
  @use '@/styles/variables.scss' as *;

  .curated-deal-tiles-grid {
    display: grid;
    column-gap: 2.4rem;
    grid-template-columns: repeat(auto-fill, minmax(33rem, 1fr));
  }

  .loader-container {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 4.8rem;
    font-size: 1.2em;
  }
</style>
